import { CloudFlowNodeType } from "@doitintl/cmp-models";
import { Box, Chip, ClickAwayListener, List, ListItem, Stack, Typography, useTheme } from "@mui/material";
import { type Node } from "@xyflow/react";

import { cloudflowTexts } from "../../../../assets/texts";
import { cmpBaseColors } from "../../../../cmpBaseColors";
import { useDarkThemeCheck } from "../../../../Components/hooks/useDarkThemeCheck";
import { type RFNode } from "../../types";
import { RoundedNodeIcon } from "../Node/NodeIcon";
import { getNodeTitle } from "../utils/nodeUtils";

type Props = {
  nodesWithErrors: Node<RFNode<CloudFlowNodeType>>[];
  isExpanded: boolean;
  onErrorNodeClick: (nodeId: string) => void;
  onClose: () => void;
};

const getNodeErrorMessages = (node: Node<RFNode<CloudFlowNodeType>>): string[] => {
  if (node.type === CloudFlowNodeType.START_STEP) {
    return [cloudflowTexts.STATUS.TRIGGER_ERROR] as string[];
  }

  return Object.values(node.data.nodeData.errorMessages || {});
};

const ErrorMessage = ({ message }: { message: string }) => (
  <ListItem
    data-cy="node-error-message"
    sx={{
      display: "flex",
      alignItems: "center",
      p: 0,
      "&:before": {
        content: '"•"',
        mr: 1,
        color: "text.secondary",
      },
    }}
  >
    <Typography
      variant="body2"
      sx={{
        fontWeight: 400,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
      color="text.secondary"
    >
      {message}
    </Typography>
  </ListItem>
);

const NodeInfo = ({ node }: { node: Node<RFNode<CloudFlowNodeType>> }) => {
  const isDarkMode = useDarkThemeCheck();

  return (
    <Stack direction="column" justifyContent="space-between" gap={0.5} width="100%">
      <Stack direction="row" alignItems="center" gap={0.5}>
        <RoundedNodeIcon node={node.data} />
        <Chip
          label={getNodeTitle(node.data)}
          size="small"
          sx={{
            background: isDarkMode ? cmpBaseColors.backgroundDark : cmpBaseColors.backgroundLight,
            color: "text.primary",
          }}
        />
      </Stack>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
          pl: 0.5,
        }}
      >
        {`${node.data.stepNumber}. ${node.data.nodeData.name}`}
      </Typography>
    </Stack>
  );
};

const ErrorNode = ({ node, onClick }: { node: Node<RFNode<CloudFlowNodeType>>; onClick: () => void }) => (
  <ListItem
    data-cy={`error-node-${node.id}`}
    onClick={onClick}
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "action.hover",
      },
    }}
  >
    <NodeInfo node={node} />
    <List
      sx={{
        pl: 1,
        pt: 0.5,
        width: "100%",
      }}
    >
      {getNodeErrorMessages(node).map((errorMessage, index) => (
        <ErrorMessage key={index} message={errorMessage} />
      ))}
    </List>
  </ListItem>
);

const NodeErrorMenu = ({ nodesWithErrors, isExpanded, onErrorNodeClick, onClose }: Props) => {
  const { shadows } = useTheme();
  if (!isExpanded) return null;
  return (
    <ClickAwayListener onClickAway={onClose}>
      <Box
        data-cy="node-error-menu"
        position="absolute"
        top={45}
        width={250}
        maxHeight={500}
        zIndex={2}
        overflow="auto"
        sx={{
          backgroundColor: "general.backgroundDefault",
          boxShadow: shadows?.[24],
          borderRadius: 1,
        }}
      >
        <List sx={{ pt: 1 }}>
          {nodesWithErrors.map((node) => (
            <ErrorNode
              key={node.id}
              node={node}
              onClick={() => {
                onErrorNodeClick(node.id);
              }}
            />
          ))}
        </List>
      </Box>
    </ClickAwayListener>
  );
};

export default NodeErrorMenu;
